import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { differenceInDays, parseISO } from "date-fns";

interface TenantColors {
  light: {
    primary: string;
    secondary: string;
  };
  dark: {
    primary: string;
    secondary: string;
  };
}

interface TenantImages {
  logo: string | null;
  login_background: string | null;
}

interface SubscriptionTransaction {
  id: string;
  tenant_id: string;
  transaction_type: string;
  payment_method: string | null;
  payment_type: string | null;
  subscription_plan_id: string | null;
  amount: number | null;
  status: string;
  payment_date: string | null;
  expiration_date: string | null; // NULL = lifetime
  metadata: any;
  created_at: string;
  updated_at: string;
}

interface TenantContextValue {
  // Dados básicos
  tenantId: string | null;
  tenantName: string | null;
  tenantSlug: string | null;
  isAdminSaas: boolean;
  isImpersonating: boolean;
  tenantColors: TenantColors | null;
  tenantImages: TenantImages | null;
  loading: boolean;
  transactionsLoading: boolean;
  
  // Dados de assinatura (do tenant)
  subscriptionStatus: 'trial' | 'active' | 'cancelled' | 'suspended';
  subscriptionPlanId: string | null;
  subscriptionType: 'regular' | 'cortesia' | 'testers' | 'promotion' | null;
  subscriptionPaymentMethod: 'credit_card' | 'pix' | 'manual' | string | null;
  subscriptionNextBillingDate: string | null;
  subscriptionLastPaymentDate: string | null;
  trialDaysRemaining: number | null;
  
  // Métodos centralizados de verificação de assinatura
  hasActiveSubscription: () => boolean;
  canAccessProtectedRoute: () => boolean;
  getDaysUntilExpiration: () => number | null;
  isSubscriptionExpiringSoon: (daysThreshold?: number) => boolean;
  isSubscriptionLifetime: () => boolean;
  getActiveTransactions: () => SubscriptionTransaction[] | null;
  getMostRecentTransaction: () => SubscriptionTransaction | null;
  refreshTenant: () => Promise<void>;
  startImpersonation: (tenantId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [isAdminSaas, setIsAdminSaas] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedTenantId, setImpersonatedTenantId] = useState<string | null>(null);
  const [tenantColors, setTenantColors] = useState<TenantColors | null>(null);
  const [tenantImages, setTenantImages] = useState<TenantImages | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'trial' | 'active' | 'cancelled' | 'suspended' | null>(null);
  const [subscriptionPlanId, setSubscriptionPlanId] = useState<string | null>(null);
  const [subscriptionType, setSubscriptionType] = useState<'regular' | 'cortesia' | 'testers' | 'promotion' | null>('regular');
  const [subscriptionPaymentMethod, setSubscriptionPaymentMethod] = useState<string | null>(null);
  const [subscriptionNextBillingDate, setSubscriptionNextBillingDate] = useState<string | null>(null);
  const [subscriptionLastPaymentDate, setSubscriptionLastPaymentDate] = useState<string | null>(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Query para buscar transações ativas (não expiradas ou lifetime)
  const { data: activeTransactions, refetch: refetchTransactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ["active-subscription-transactions", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from("subscription_transactions")
        .select("*")
        .eq("tenant_id", tenantId)
        .eq("status", "approved")
        .or(`expiration_date.is.null,expiration_date.gte.${now}`)
        .order("expiration_date", { ascending: false, nullsFirst: false })
        .order("payment_date", { ascending: false, nullsFirst: false });
      
      if (error) throw error;
      return (data || []) as SubscriptionTransaction[];
    },
    enabled: !!tenantId && !loading,
    refetchInterval: 60000, // Refetch a cada minuto para verificar expirações
  });

  const forceRefreshRef = React.useRef<boolean>(false);

  const fetchTenantData = async () => {
    try {
      if (isImpersonating && impersonatedTenantId) {
        return;
      }
      // Só mostrar loading na primeira carga ou se não temos dados
      const shouldShowLoading = isInitialLoadRef.current || (!lastTenantIdRef.current && !loading);
      if (shouldShowLoading) {
        setLoading(true);
        isInitialLoadRef.current = false;
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Se já tínhamos dados e o usuário saiu, limpar tudo
        if (lastTenantIdRef.current) {
          setTenantId(null);
          setTenantName(null);
          setTenantSlug(null);
          setIsAdminSaas(false);
          setTenantColors(null);
          setTenantImages(null);
          lastTenantIdRef.current = null;
        }
        lastUserIdRef.current = null;
        setLoading(false);
        return;
      }
      
      // Atualizar ref com o ID do usuário atual
      lastUserIdRef.current = user.id;

      // Buscar profile do usuário com type assertion
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select(`
          tenant_id,
          role_id,
          roles:role_id (
            slug
          )
        `)
        .eq("id", user.id)
        .maybeSingle() as any;

      if (profileError) {
        console.error("Erro ao buscar profile:", profileError);
        setLoading(false);
        return;
      }

      // Verificar se é Admin SaaS
      const roleSlug = profile?.roles?.slug;
      setIsAdminSaas(roleSlug === "admin_saas");

      if (!profile?.tenant_id) {
        // Se não tem tenant_id mas tinha antes, limpar
        if (lastTenantIdRef.current) {
          setTenantId(null);
          setTenantName(null);
          setTenantSlug(null);
          setIsAdminSaas(false);
          setTenantColors(null);
          setTenantImages(null);
          lastTenantIdRef.current = null;
        }
        setLoading(false);
        return;
      }

      // Verificar se o tenant_id mudou usando apenas a ref
      if (lastTenantIdRef.current === profile.tenant_id && lastTenantIdRef.current !== null && !forceRefreshRef.current) {
        // Mesmo tenant, não precisa recarregar dados do tenant
        // Os dados já estão carregados, então apenas garantir que o estado está sincronizado
        setLoading(false);
        return;
      }
      
      // Atualizar ref com o tenant_id atual ANTES de buscar dados
      const newTenantId = profile.tenant_id;
      lastTenantIdRef.current = newTenantId;
      setTenantId(newTenantId);

      const { data: tenant, error: tenantError } = await supabase
        .from("tenants" as any)
        .select("name, slug, colors, images, subscription_status, subscription_plan_id, subscription_type, subscription_payment_method, subscription_next_billing_date, subscription_last_payment_date, trial_ends_at")
        .eq("id", profile.tenant_id)
        .maybeSingle() as any;

      if (tenantError) {
        console.error("Erro ao buscar tenant:", tenantError);
        setLoading(false);
        return;
      }

      if (!tenant) {
        console.error("Tenant não encontrado");
        setLoading(false);
        return;
      }

      setTenantName(tenant.name);
      setTenantSlug(tenant.slug);
      setTenantColors(tenant.colors as TenantColors);
      setTenantImages(tenant.images as TenantImages);
      setSubscriptionStatus(tenant.subscription_status || 'trial');
      setSubscriptionPlanId(tenant.subscription_plan_id ?? null);
      setSubscriptionType(tenant.subscription_type || 'regular');
      setSubscriptionPaymentMethod(tenant.subscription_payment_method ?? null);
      setSubscriptionNextBillingDate(tenant.subscription_next_billing_date ?? null);
      setSubscriptionLastPaymentDate(tenant.subscription_last_payment_date ?? null);
      
      // Calcular dias restantes do trial
      if (tenant.trial_ends_at) {
        const trialEnd = new Date(tenant.trial_ends_at);
        const now = new Date();
        const diffTime = trialEnd.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setTrialDaysRemaining(diffDays > 0 ? diffDays : 0);
      } else {
        setTrialDaysRemaining(null);
      }

      // Refetch transações após atualizar tenant
      refetchTransactions();

    } catch (error) {
      console.error("Erro ao carregar dados do tenant:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados da empresa.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTenantById = async (targetTenantId: string) => {
    try {
      setLoading(true);
      const { data: tenant, error: tenantError } = await supabase
        .from("tenants" as any)
        .select("name, slug, colors, images, subscription_status, subscription_plan_id, subscription_type, subscription_payment_method, subscription_next_billing_date, subscription_last_payment_date, trial_ends_at")
        .eq("id", targetTenantId)
        .maybeSingle() as any;

      if (tenantError) {
        throw tenantError;
      }

      if (!tenant) {
        throw new Error("Tenant não encontrado");
      }

      lastTenantIdRef.current = targetTenantId;
      setTenantId(targetTenantId);
      setTenantName(tenant.name);
      setTenantSlug(tenant.slug);
      setTenantColors(tenant.colors as TenantColors);
      setTenantImages(tenant.images as TenantImages);
      setSubscriptionStatus(tenant.subscription_status || 'trial');
      setSubscriptionPlanId(tenant.subscription_plan_id ?? null);
      setSubscriptionType(tenant.subscription_type || 'regular');
      setSubscriptionPaymentMethod(tenant.subscription_payment_method ?? null);
      setSubscriptionNextBillingDate(tenant.subscription_next_billing_date ?? null);
      setSubscriptionLastPaymentDate(tenant.subscription_last_payment_date ?? null);

      if (tenant.trial_ends_at) {
        const trialEnd = new Date(tenant.trial_ends_at);
        const now = new Date();
        const diffTime = trialEnd.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setTrialDaysRemaining(diffDays > 0 ? diffDays : 0);
      } else {
        setTrialDaysRemaining(null);
      }

      refetchTransactions();
    } catch (error: any) {
      console.error("Erro ao carregar tenant por ID:", error);
      toast({
        title: "Erro",
        description: error?.message || "Falha ao carregar dados do cliente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Refs para rastrear estado atual e evitar recarregamentos desnecessários
  const lastUserIdRef = React.useRef<string | null>(null);
  const lastTenantIdRef = React.useRef<string | null>(null);
  const isInitialLoadRef = React.useRef<boolean>(true);
  
  useEffect(() => {
    fetchTenantData();

    // Escutar mudanças na sessão, mas só recarregar se o user realmente mudou
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentUserId = session?.user?.id || null;
      
      // Ignorar eventos de TOKEN_REFRESHED que acontecem quando a aba volta ao foco
      // Só recarregar se houver mudança real na sessão
      if (event === 'TOKEN_REFRESHED') {
        // Se é o mesmo usuário e já carregamos dados, não precisa recarregar
        if (currentUserId === lastUserIdRef.current && lastTenantIdRef.current !== null) {
          return;
        }
      }
      
      // Para outros eventos (SIGNED_IN, SIGNED_OUT, USER_UPDATED), recarregar
      // Atualizar ref antes de recarregar para evitar loops
      lastUserIdRef.current = currentUserId;
      fetchTenantData();
    });

    return () => subscription.unsubscribe();
  }, []);

  // ============================================================================
  // MÉTODOS CENTRALIZADOS DE VERIFICAÇÃO DE ASSINATURA
  // ============================================================================

  /**
   * Verifica se o tenant tem assinatura ativa baseado em:
   * 1. subscription_status === 'active' E subscription_plan_id !== null
   * 2. OU tem transações ativas (não expiradas ou lifetime)
   * 3. OU é tipo especial (cortesia/testers) sem expiração
   */
  const hasActiveSubscription = useMemo(() => {
    return (): boolean => {
      // Verificar status do tenant
      const hasActivePlan = subscriptionPlanId !== null && subscriptionStatus === 'active';
      
      // Verificar se é tipo especial (cortesia/testers) - sempre ativo
      const isSpecialPlan = subscriptionType === 'cortesia' || subscriptionType === 'testers';
      
      // Verificar se tem transações ativas (não expiradas ou lifetime)
      const hasActiveTransactions = activeTransactions && activeTransactions.length > 0;
      
      return hasActivePlan || isSpecialPlan || hasActiveTransactions || false;
    };
  }, [subscriptionPlanId, subscriptionStatus, subscriptionType, activeTransactions]);

  /**
   * Verifica se pode acessar rotas protegidas por assinatura
   */
  const canAccessProtectedRoute = useMemo(() => {
    return (): boolean => {
      // Verificar se tem assinatura ativa
      if (hasActiveSubscription()) return true;
      
      // Verificar se é tipo especial (cortesia/testers)
      const isSpecialPlan = subscriptionType === 'cortesia' || subscriptionType === 'testers';
      if (isSpecialPlan) return true;
      
      return false;
    };
  }, [hasActiveSubscription, subscriptionType]);

  /**
   * Retorna quantos dias faltam para a assinatura expirar
   * Retorna null se for lifetime ou se não houver data de expiração
   */
  const getDaysUntilExpiration = useMemo(() => {
    return (): number | null => {
      // Verificar se há transação lifetime
      const lifetimeTransaction = activeTransactions?.find(
        (t) => t.expiration_date === null
      );
      
      if (lifetimeTransaction) return null; // Lifetime
      
      // Buscar transação com expiration_date mais distante
      const transactionsWithExpiration = activeTransactions
        ?.filter((t) => t.expiration_date !== null)
        .sort((a, b) => {
          const dateA = parseISO(a.expiration_date!);
          const dateB = parseISO(b.expiration_date!);
          return dateB.getTime() - dateA.getTime();
        });
      
      if (transactionsWithExpiration && transactionsWithExpiration.length > 0) {
        const expirationDate = parseISO(transactionsWithExpiration[0].expiration_date!);
        const now = new Date();
        const days = differenceInDays(expirationDate, now);
        return days > 0 ? days : 0;
      }
      
      // Fallback: usar subscription_next_billing_date
      if (subscriptionNextBillingDate) {
        const nextBilling = parseISO(subscriptionNextBillingDate);
        const now = new Date();
        const days = differenceInDays(nextBilling, now);
        return days > 0 ? days : 0;
      }
      
      // Se é tipo especial sem data definida, considerar lifetime
      if ((subscriptionType === 'cortesia' || subscriptionType === 'testers') && !subscriptionNextBillingDate) {
        return null; // Lifetime
      }
      
      return null; // Sem expiração definida
    };
  }, [activeTransactions, subscriptionType, subscriptionNextBillingDate]);

  /**
   * Verifica se a assinatura está próxima de expirar
   */
  const isSubscriptionExpiringSoon = useMemo(() => {
    return (daysThreshold: number = 7): boolean => {
      const daysUntilExpiration = getDaysUntilExpiration();
      if (daysUntilExpiration === null) return false; // Lifetime não expira
      return daysUntilExpiration <= daysThreshold;
    };
  }, [getDaysUntilExpiration]);

  /**
   * Verifica se a assinatura é lifetime (sem vencimento)
   */
  const isSubscriptionLifetime = useMemo(() => {
    return (): boolean => {
      // Verificar se há transação lifetime
      const hasLifetimeTransaction = activeTransactions?.some(
        (t) => t.expiration_date === null
      ) || false;
      
      // Verificar se é tipo especial sem expiração definida
      if ((subscriptionType === 'cortesia' || subscriptionType === 'testers') && !subscriptionNextBillingDate) {
        return true;
      }
      
      return hasLifetimeTransaction;
    };
  }, [activeTransactions, subscriptionType, subscriptionNextBillingDate]);

  /**
   * Retorna todas as transações ativas (não expiradas)
   */
  const getActiveTransactions = useMemo(() => {
    return (): SubscriptionTransaction[] | null => {
      return activeTransactions || null;
    };
  }, [activeTransactions]);

  /**
   * Retorna a transação mais recente (ativa)
   */
  const getMostRecentTransaction = useMemo(() => {
    return (): SubscriptionTransaction | null => {
      if (!activeTransactions || activeTransactions.length === 0) return null;
      
      // Ordenar por expiration_date (lifetime primeiro) ou payment_date
      const sorted = [...activeTransactions].sort((a, b) => {
        // Lifetime (null) vem primeiro
        if (a.expiration_date === null && b.expiration_date !== null) return -1;
        if (a.expiration_date !== null && b.expiration_date === null) return 1;
        
        // Se ambas têm expiration_date, ordenar por data mais distante
        if (a.expiration_date && b.expiration_date) {
          const dateA = parseISO(a.expiration_date);
          const dateB = parseISO(b.expiration_date);
          return dateB.getTime() - dateA.getTime();
        }
        
        // Fallback: ordenar por payment_date
        if (a.payment_date && b.payment_date) {
          const dateA = parseISO(a.payment_date);
          const dateB = parseISO(b.payment_date);
          return dateB.getTime() - dateA.getTime();
        }
        
        return 0;
      });
      
      return sorted[0] || null;
    };
  }, [activeTransactions]);

  const refreshTenantWrapper = async () => {
    try {
      forceRefreshRef.current = true;
      await fetchTenantData();
    } finally {
      forceRefreshRef.current = false;
    }
  };

  return (
    <TenantContext.Provider
      value={{
        tenantId,
        tenantName,
        tenantSlug,
        isAdminSaas,
        isImpersonating,
        tenantColors,
        tenantImages,
        loading,
        transactionsLoading: transactionsLoading && !!tenantId,
        subscriptionStatus: subscriptionStatus || 'trial',
        subscriptionPlanId,
        subscriptionType,
        subscriptionPaymentMethod,
        subscriptionNextBillingDate,
        subscriptionLastPaymentDate,
        trialDaysRemaining,
        // Métodos centralizados
        hasActiveSubscription,
        canAccessProtectedRoute,
        getDaysUntilExpiration,
        isSubscriptionExpiringSoon,
        isSubscriptionLifetime,
        getActiveTransactions,
        getMostRecentTransaction,
        refreshTenant: refreshTenantWrapper,
        startImpersonation: async (targetId: string) => {
          if (!isAdminSaas) return;
          setIsImpersonating(true);
          setImpersonatedTenantId(targetId);
          await loadTenantById(targetId);
          toast({
            title: "Impersonate ativado",
            description: "Você está atuando como o cliente selecionado.",
          });
        },
        stopImpersonation: async () => {
          setIsImpersonating(false);
          setImpersonatedTenantId(null);
          await fetchTenantData();
          toast({
            title: "Impersonate desativado",
            description: "Você retornou ao contexto original.",
          });
        },
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant deve ser utilizado dentro de um TenantProvider");
  }
  return context;
};
